import React, { useContext } from "react";
import PropTypes from "prop-types";
import { OverheadsEntryForm, PageLayout, useTranslator } from "@sia/style-guide";
import { DataCollectionContext } from "contexts/DataCollectionContext";
import { PreviousSurveyOverheads } from "../../TestMode/PreviousSurveyData";
import "./../SectionContent.scss";
import "./SurveyOverheadsSection.scss";

const BADGE_ICONS = { saved: "✓", success: "✓", error: "!" };
const EMPTY_PROGRESS = { completed: 0, total: 0, remaining: 0, percentage: 0, isComplete: false };

/* eslint-disable react/prop-types */
const SurveyOverheadsSection = ({
  items,
  fieldsByKey,
  undoOwnerFieldKey,
  badge,
  feedbackClock,
  progress,
  parentChildrenTotalErrors,
  onFieldChange,
  onFieldBlur,
  onFieldAction,
}) => {
  const translate = useTranslator();
  const { isInitialLoad, OnReviewOrConcluded, isSurveyFinished } = useContext(DataCollectionContext);
  const formDisabled = isSurveyFinished || OnReviewOrConcluded;
  const safeProgress = progress || EMPTY_PROGRESS;
  const progressLabel = translate("benchmarking.survey.company-overhead.mandatory-fields-progress");

  return (
    <PageLayout>
      <div></div>
      <div className="section-content-entryform-page section-content-entryform-page--wide survey-overheads-section">
        <div className="overheads-section-status">
          <div className="overheads-progress">
            <span className="overheads-progress__label">
              {progressLabel}: {safeProgress.completed}/{safeProgress.total}
            </span>
            <div
              className="overheads-progress__track"
              role="progressbar"
              aria-label={progressLabel}
              aria-valuemin="0"
              aria-valuemax={safeProgress.total}
              aria-valuenow={safeProgress.completed}
            >
              <span
                className={`overheads-progress__fill${safeProgress.isComplete ? " overheads-progress__fill--complete" : ""}`}
                style={{ width: `${safeProgress.percentage}%` }}
              />
            </div>
          </div>
          <div className={`overheads-save-badge overheads-save-badge--${badge.kind}`} role="status" aria-live="polite">
            <span className={`overheads-save-badge__icon overheads-save-badge__icon--${badge.kind}`} aria-hidden>
              {badge.kind === "saving" ? "" : BADGE_ICONS[badge.kind]}
            </span>
            <span>{translate(badge.labelKey)}</span>
          </div>
        </div>
        <OverheadsEntryForm
          items={items}
          fieldsByKey={fieldsByKey}
          undoOwnerFieldKey={undoOwnerFieldKey}
          parentChildrenTotalErrors={parentChildrenTotalErrors}
          onFieldChange={onFieldChange}
          onFieldBlur={onFieldBlur}
          onFieldAction={onFieldAction}
          isInitialLoad={isInitialLoad}
          translate={translate}
          allowNegativesFormatting={false}
          invalidText={translate("benchmarking.survey.invalid-text")}
          isDisabled={formDisabled}
          feedbackClock={feedbackClock}
        />
        <PreviousSurveyOverheads />
      </div>
      <div></div>
    </PageLayout>
  );
};

SurveyOverheadsSection.propTypes = {
  items: PropTypes.array,
  fieldsByKey: PropTypes.object.isRequired,
  undoOwnerFieldKey: PropTypes.string,
  badge: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  feedbackClock: PropTypes.number,
  progress: PropTypes.shape({
    completed: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    remaining: PropTypes.number,
    percentage: PropTypes.number.isRequired,
    isComplete: PropTypes.bool.isRequired,
  }),
  parentChildrenTotalErrors: PropTypes.object,
  onFieldChange: PropTypes.func.isRequired,
  onFieldBlur: PropTypes.func.isRequired,
  onFieldAction: PropTypes.func.isRequired,
};

SurveyOverheadsSection.defaultProps = {
  items: [],
  undoOwnerFieldKey: null,
  feedbackClock: Date.now(),
  progress: EMPTY_PROGRESS,
  parentChildrenTotalErrors: {},
};

export default SurveyOverheadsSection;
